import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/user/_layout/attendance/timesheet")({
  component: RouteComponent,
});

function RouteComponent() {
  return <MyTimesheet />;
}

import { useEffect, useMemo, useState } from "react";
import { dayjs } from "@/lib/date-utils";
import type { DateRange } from "react-day-picker";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import { MultiSelectFilter } from "@/components/crud/filters/MultiSelectFilter";
import {
  timeSheetStatusFilterOptions,
  type TimeSheetStatusFilter,
} from "@/lib/timesheet-status-filter";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import useFetchMyTimeSheet, {
  type FetchMyTimeSheetsParams,
} from "@/hooks/api/timesheet/useFetchMyTimeSheets";
import { TimeSheetTable } from "@/components/crud/table/TimeSheetTable";
import { RequestAdjustmentDialog } from "@/components/crud/modals/RequestAdjustmentDialog";
import { OvertimeRequestDialog } from "@/components/user/overtime/OvertimeRequestDialog";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import type { TimeSheet } from "@/types/timesheet";
import { FilePen } from "lucide-react";

function MyTimesheet() {
  const [showFilters, setShowFilters] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<TimeSheetStatusFilter[]>([]);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const timeSheetParams: FetchMyTimeSheetsParams = {
    page,
    page_size: pageSize,
    status: statusFilter.length ? statusFilter : undefined,
  };
  if (range?.from && range?.to) {
    timeSheetParams.start_date = dayjs(range.from).format("YYYY-MM-DD");
    timeSheetParams.end_date = dayjs(range.to).format("YYYY-MM-DD");
  }
  const myTimeSheetsQuery = useFetchMyTimeSheet(timeSheetParams);
  const myTimeSheets = myTimeSheetsQuery.data?.results ?? null;
  const count = myTimeSheetsQuery.data?.count ?? 0;
  const loading = myTimeSheetsQuery.isPending;

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize],
  );
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [range, statusFilter, setPage]);

  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [selectedTimesheetForAdjustment, setSelectedTimesheetForAdjustment] =
    useState<TimeSheet | null>(null);

  const handleRequestAdjustment = (ts: TimeSheet) => {
    setSelectedTimesheetForAdjustment(ts);
    setAdjustmentDialogOpen(true);
  };

  // Top-level button: let the employee pick the date themselves, instead
  // of finding the row for it in the (possibly filtered/paginated) table.
  const handleRequestAdjustmentForDate = () => {
    setSelectedTimesheetForAdjustment(null);
    setAdjustmentDialogOpen(true);
  };

  const overtimeEnabled = useOrgSettingsStore(
    (s) => s.settings?.overtime_approval_required,
  );
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [selectedTimesheetForOvertime, setSelectedTimesheetForOvertime] =
    useState<TimeSheet | null>(null);

  const handleRequestOvertime = (ts: TimeSheet) => {
    setSelectedTimesheetForOvertime(ts);
    setOvertimeDialogOpen(true);
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="Timesheets"
          description="View your attendance timesheet records."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleRequestAdjustmentForDate}
          addLabel="Request Adjustment"
          ButtonIcon={FilePen}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            setRange(undefined);
            setStatusFilter([]);
          }}
        >
          <MultiSelectFilter
            placeholder="Filter by status"
            options={timeSheetStatusFilterOptions}
            values={statusFilter}
            onChange={setStatusFilter}
          />

          <DateSelector
            value={range}
            onApply={setRange}
            onClear={() => setRange(undefined)}
            label="Filter by date range"
          />
        </FiltersPanel>

        <TimeSheetTable
          timesheets={myTimeSheets ?? []}
          isLoading={loading}
          onRefresh={() => myTimeSheetsQuery.refetch()}
          hideUserColumn
          showLateColumn
          showAdjustments
          onRequestAdjustment={handleRequestAdjustment}
          onRequestOvertime={
            overtimeEnabled ? handleRequestOvertime : undefined
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={myTimeSheetsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <RequestAdjustmentDialog
          isOpen={adjustmentDialogOpen}
          onClose={() => setAdjustmentDialogOpen(false)}
          timesheet={selectedTimesheetForAdjustment}
          onSuccess={() => myTimeSheetsQuery.refetch()}
          allowDateSelection
        />

        <OvertimeRequestDialog
          open={overtimeDialogOpen}
          onOpenChange={setOvertimeDialogOpen}
          initialDate={selectedTimesheetForOvertime?.date}
          onSuccess={() => myTimeSheetsQuery.refetch()}
        />
      </div>
    </SidebarInset>
  );
}
